import { Paperclip } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { useToast } from '@/components/ui/toast';
import type { InboundEmailDTO } from '@/features/inbox/api';
import { useAssignMutation, useInbox } from '@/features/inbox/hooks';
import { useServices, useActiveCnpjId } from '@/features/services/hooks';
import { ApiError } from '@/lib/api';

export default function InboxPage() {
  const query = useInbox();
  const [selected, setSelected] = useState<InboundEmailDTO | null>(null);
  const emails = query.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="font-display text-2xl font-semibold">Inbox</h1>
        <p className="text-sm text-(--color-fg-muted)">
          E-mails que o classificador automático não conseguiu atribuir. Escolha o serviço manualmente.
        </p>
      </header>

      {query.isError ? (
        <p
          role="alert"
          className="rounded-md border border-(--color-error) p-3 text-sm text-(--color-error)"
        >
          Não foi possível carregar a caixa de entrada.
        </p>
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-(--color-fg-muted)">Carregando…</p>
      ) : emails.length === 0 ? (
        <Card>
          <p className="text-sm text-(--color-fg-muted)">
            Nenhum e-mail ambíguo por aqui. Tudo classificado!
          </p>
        </Card>
      ) : (
        <ul className="flex flex-col gap-3">
          {emails.map((e) => (
            <EmailItem key={e.id} email={e} onAssign={() => setSelected(e)} />
          ))}
        </ul>
      )}

      <AssignDialog
        email={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function EmailItem({
  email,
  onAssign,
}: {
  email: InboundEmailDTO;
  onAssign: () => void;
}) {
  const attachments = email.attachmentPaths.length;
  const confidence =
    email.classificationConfidence != null
      ? `${Math.round(email.classificationConfidence * 100)}%`
      : '—';

  return (
    <li>
      <Card className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium">{email.subject || '(sem assunto)'}</span>
            {attachments > 0 ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-(--color-border) px-2 py-0.5 text-xs text-(--color-fg-muted)">
                <Paperclip className="h-3 w-3" aria-hidden /> {attachments}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 truncate text-sm text-(--color-fg-muted)">
            De: {email.fromEmail}
          </p>
          <p className="mt-0.5 text-xs text-(--color-fg-muted)">
            Recebido em{' '}
            {new Date(email.receivedAt).toLocaleString('pt-BR')} · Confiança {confidence}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button type="button" onClick={onAssign}>
            Atribuir a serviço
          </Button>
        </div>
      </Card>
    </li>
  );
}

function AssignDialog({
  email,
  onClose,
}: {
  email: InboundEmailDTO | null;
  onClose: () => void;
}) {
  const { cnpjId } = useActiveCnpjId();
  const services = useServices(cnpjId);
  const mutation = useAssignMutation();
  const toast = useToast();
  const [serviceId, setServiceId] = useState<string>('');

  const open = !!email;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setServiceId('');
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Atribuir a serviço</DialogTitle>
          <p className="text-sm text-(--color-fg-muted)">
            {email?.subject || '(sem assunto)'} — de {email?.fromEmail}
          </p>
        </DialogHeader>
        <DialogBody>
          <FormField id="inbox-service" label="Serviço" required>
            <select
              id="inbox-service"
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              className="h-11 w-full rounded-md border border-(--color-border) bg-(--color-surface) px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)"
            >
              <option value="">Selecione…</option>
              {(services.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </FormField>
          <p className="mt-3 text-xs text-(--color-fg-muted)">
            Ao atribuir, criamos um registro fiscal do mês e encaminhamos o anexo ao OCR.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!serviceId || !email || mutation.isPending}
            onClick={async () => {
              if (!email || !serviceId) return;
              try {
                await mutation.mutateAsync({ emailId: email.id, serviceId });
                toast.show({ title: 'E-mail atribuído.', variant: 'success' });
                setServiceId('');
                onClose();
              } catch (err) {
                const msg =
                  err instanceof ApiError ? err.message : 'Falha ao atribuir e-mail';
                toast.show({ title: msg, variant: 'error' });
              }
            }}
          >
            Confirmar atribuição
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
